import React, { useState } from "react";
import DocViewer from '~/components/Shared/DocViewer';
import { useAuth } from "~/context/auth";
import Agreement from "./Agreement";
import PaperCopyRequest from "./PaperCopyRequest";
import EsignConfirmation from "../Partial/EsignConfirmation";
import useEsignConsent from "~/hooks/useEsignConsent";
import OrderDoc from "~/model/orderDoc";
import User from "~/model/user";

type TSignProps = {
  id: number;
  goBack: () => void;
}

const Sign = ({ id, goBack }: TSignProps) => {

  const { user: authUser } = useAuth();

  // by default, hide the signature fields so the user can read the doc
  const [isReadOnly, setIsReadOnly] = useState(true);
  // instant signing is enabled
  const [isInstantSigningInProgress, setIsInstantSigningInProgress] = useState(false);
  // order doc
  const [orderDoc, setOrderDoc] = useState<OrderDoc | null>(null);
  // user agreed once
  const [userAgreedOnce, setUserAgreedOnce] = useState(false);

  // Use ESIGN consent hook
  const {
    isAgreementVisible,
    setIsAgreementVisible,
    isAgreementConfirmed,
    setIsAgreementConfirmed,
    isPaperCopyRequestClosed,
    isDealerAgreementVisible,
    setIsDealerAgreementVisible,
    orderSignerAddress,
    showAgreementFlow,
    displayAgreeModal,
    dealerNeedsConsent,
    handleEsignConsent,
    handlePaperCopyConfirm,
    handlePaperCopyCancel,
  } = useEsignConsent({
    docId: id,
    onConsentSuccess: () => {
      setUserAgreedOnce(true);
      if (orderDoc?.isInstantSignEnabled) {
        setIsInstantSigningInProgress(true);
      } else {
        setIsReadOnly(false);
      }
    }
  });

  // show the PDF viewer:
  // For signers 1 and 2: only if agreement is not visible AND (PaperCopyRequest was never opened OR PaperCopyRequest was closed)
  // For dealer: always show (so modal can appear over it)
  const showPDFViewer = dealerNeedsConsent || (!isAgreementVisible && (isPaperCopyRequestClosed || !isAgreementConfirmed));

  // Override handlePaperCopyConfirm to also set isReadOnly
  const handlePaperCopyConfirmWithReadOnly = async (value: boolean) => {
    if (!orderDoc?.isInstantSignEnabled) {
      setIsReadOnly(false);
    }
    await handlePaperCopyConfirm(value);
  };

  const handleSigningStarted = (orderDoc: OrderDoc, onlyView = false) => {
    // we save the order doc to the state because we need to know if instant signing is enabled
    setOrderDoc(orderDoc);

    if (!userAgreedOnce) {
      if (displayAgreeModal) {
        setIsDealerAgreementVisible(true);
        return;
      } else if (showAgreementFlow) {
        setIsAgreementVisible(true);
        return;
      }
    }

    // if instant signing is enabled, set the flag and keep the doc readonly
    if (orderDoc.isInstantSignEnabled && !onlyView) {
      return setIsInstantSigningInProgress(true);
    }
    return setIsReadOnly(false);
  };

  const handleDealerConsent = async () => {
    setIsDealerAgreementVisible(false);
    await handleEsignConsent();
  };

  return (
    <React.Fragment>
      {/* Show Agreement Page */}
      {isAgreementVisible &&
        <Agreement
          orderSigner={authUser as User}
          onConfirm={() => {
            setIsAgreementConfirmed(true);
            setIsAgreementVisible(false);
          }}
          onCancel={() => {
            setIsAgreementVisible(false);
            goBack();
          }
          }
        />
      }

      {/* Show Paper Copy request screen */}
      {isAgreementConfirmed && !isPaperCopyRequestClosed && (
        <PaperCopyRequest
          orderSignerAddress={orderSignerAddress}
          onConfirm={handlePaperCopyConfirmWithReadOnly}
          onCancel={handlePaperCopyCancel}
        />
      )}

      {/* Show Agreement Modal - For dealers only */}
      {isDealerAgreementVisible && showPDFViewer && (
        <EsignConfirmation
          onConfirm={handleDealerConsent}
          onCancel={() => {
            setIsDealerAgreementVisible(false);
            goBack();
          }}
        >
          <div className="align-justify">
            The parties agree that this Agreement and all notices and disclosures made or given in connection with this Agreement may be created, executed, delivered and retained electronically.<br />
            As such, the parties agree that this Agreement and any related documents may be signed electronically, and that the electronic signatures appearing on this Agreement or any related documents shall have the same legal effect for all purposes, including validity, enforceability and admissibility, as a handwritten signature.<br />
            If you are a car buyer, you may access a copy of your digital records by contacting<a href="mailto: cs@mavisgn.com"> cs@mavisgn.com</a>.<br />
            You have the right to receive a copy of the electronic record by requesting a paper copy. To opt out of electronic signatures, stop immediately and contact your dealership.<br />
            Opting out of electronic signatures may delay your car purchase.
          </div>
        </EsignConfirmation>
      )}

      {/* Show PDF Viewer */}
      {showPDFViewer && <div className="pdfwrapper e-sign">
        <DocViewer
          docId={id}
          currentSignerId={authUser?.id!}
          readOnly={isReadOnly}
          onSigningStarted={handleSigningStarted}
          isInstantSigningInProgress={isInstantSigningInProgress}
          setIsInstantSigningInProgress={setIsInstantSigningInProgress}
          onViewerClosed={() => goBack()}
          onSigningComplete={() => goBack()}
          onDocLoaded={(doc: OrderDoc) => {
            if (!doc.isInstantSignEnabled) {
              if (doc.signingId) {
                setIsReadOnly(false);
              } else {
                setIsReadOnly(true);
              }
            }
          }} />
      </div>}
    </React.Fragment>
  )
}

export default Sign;